import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { validateOptionSelection } from '@garlic/shared';
import { formatBRL } from '../lib/format';
import { Alert, Button, Textarea, cx } from '../ui/primitives';
import { Sheet } from '../ui/sheet';
import type { CartLine } from './cart';
import type { MenuCategory, MenuProduct } from './types';

export function QtyStepper({ value, onChange, min = 1, max }: { value: number; onChange: (n: number) => void; min?: number; max: number }) {
  return (
    <div className="inline-flex items-center rounded-full border border-line bg-white">
      <button aria-label="Diminuir" disabled={value <= min} onClick={() => onChange(value - 1)} className="grid size-10 place-items-center rounded-full text-ink-2 hover:bg-paper-2 disabled:opacity-40">
        <Minus className="size-4" />
      </button>
      <span className="w-8 text-center font-semibold tabular-nums" aria-live="polite">{value}</span>
      <button aria-label="Aumentar" disabled={value >= max} onClick={() => onChange(value + 1)} className="grid size-10 place-items-center rounded-full text-ink-2 hover:bg-paper-2 disabled:opacity-40">
        <Plus className="size-4" />
      </button>
    </div>
  );
}

export function ProductSheet({
  product, category, canOrder, blockedReason, onClose, onAdd,
}: {
  product: MenuProduct | null;
  category: MenuCategory | null;
  canOrder: boolean;
  blockedReason?: string;
  onClose: () => void;
  onAdd: (line: Omit<CartLine, 'key'>) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setSelected([]);
    setQty(1);
    setNotes('');
    setTouched(false);
  }, [product?.id]);

  const p = product;
  const unit = useMemo(() => {
    if (!p) return 0;
    const opts = p.optionGroups.flatMap((g) => g.options).filter((o) => selected.includes(o.id));
    return p.priceCents + opts.reduce((s, o) => s + o.priceCents, 0);
  }, [p, selected]);

  if (!p || !category) return <Sheet open={false} onClose={onClose} title="">{null}</Sheet>;

  const problem = validateOptionSelection(
    p.optionGroups.map((g) => ({ id: g.id, name: g.name, minSelect: g.minSelect, maxSelect: g.maxSelect, optionIds: g.options.map((o) => o.id) })),
    selected,
  );
  const unavailable = !p.available || !category.availableNow;

  const toggle = (groupId: string, optionId: string) => {
    const g = p.optionGroups.find((x) => x.id === groupId)!;
    const inGroup = selected.filter((id) => g.options.some((o) => o.id === id));
    if (selected.includes(optionId)) return setSelected(selected.filter((id) => id !== optionId));
    // Escolha única: troca a seleção. Múltipla: respeita o máximo.
    if (g.maxSelect === 1) return setSelected([...selected.filter((id) => !inGroup.includes(id)), optionId]);
    if (inGroup.length >= g.maxSelect) return;
    setSelected([...selected, optionId]);
  };

  const add = () => {
    setTouched(true);
    if (problem) return;
    const labels = p.optionGroups.flatMap((g) => g.options.filter((o) => selected.includes(o.id)).map((o) => o.name));
    onAdd({ productId: p.id, name: p.name, quantity: qty, optionIds: selected, optionLabels: labels, unitPriceCents: unit, notes: notes.trim() || null });
    onClose();
  };

  return (
    <Sheet
      open={!!product}
      onClose={onClose}
      title={
        <div>
          <h2 className="font-display text-xl font-semibold leading-tight">{p.name}</h2>
          <p className="mt-0.5 text-sm font-semibold text-ink-2">{formatBRL(p.priceCents)}</p>
        </div>
      }
      footer={
        canOrder && !unavailable ? (
          <div className="flex items-center gap-3">
            <QtyStepper value={qty} onChange={setQty} max={Math.max(1, p.maxQuantity)} />
            <Button variant="brand" size="lg" className="flex-1" onClick={add}>
              Adicionar · {formatBRL(unit * qty)}
            </Button>
          </div>
        ) : (
          <p className="py-1 text-center text-sm text-ink-3">{unavailable ? (p.soldOut ? 'Produto esgotado.' : 'Indisponível no momento.') : blockedReason}</p>
        )
      }
    >
      {p.imagePath && <img src={p.imagePath} alt="" className="-mx-5 -mt-4 mb-4 aspect-[16/10] w-[calc(100%+2.5rem)] max-w-none object-cover" />}
      {p.description && <p className="text-sm leading-relaxed text-ink-2">{p.description}</p>}
      {p.prepMinutes && <p className="mt-2 text-xs text-ink-3">Preparo estimado: {p.prepMinutes} min</p>}

      {p.optionGroups.map((g) => {
        const count = selected.filter((id) => g.options.some((o) => o.id === id)).length;
        const required = g.minSelect > 0;
        const invalid = touched && (count < g.minSelect || count > g.maxSelect);
        return (
          <fieldset key={g.id} className="mt-5">
            <legend className="flex w-full items-center justify-between gap-2">
              <span>
                <span className="font-semibold">{g.name}</span>
                <span className="ml-2 text-xs text-ink-3">
                  {g.maxSelect === 1 ? 'Escolha 1' : g.minSelect > 0 ? `Escolha de ${g.minSelect} a ${g.maxSelect}` : `Até ${g.maxSelect}`}
                </span>
              </span>
              <span className={cx('rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase', required ? (invalid ? 'bg-danger-50 text-danger-600' : 'bg-ink text-paper') : 'bg-paper-2 text-ink-3')}>
                {required ? 'Obrigatório' : 'Opcional'}
              </span>
            </legend>
            {g.description && <p className="mt-0.5 text-xs text-ink-3">{g.description}</p>}
            <div className="mt-2 divide-y divide-line rounded-2xl border border-line">
              {g.options.map((o) => {
                const checked = selected.includes(o.id);
                const blocked = !o.available || (!checked && g.maxSelect > 1 && count >= g.maxSelect);
                return (
                  <label key={o.id} className={cx('flex cursor-pointer items-center gap-3 px-4 py-3 text-sm', blocked && !checked && 'cursor-not-allowed opacity-50')}>
                    <input
                      type={g.maxSelect === 1 ? 'radio' : 'checkbox'}
                      name={g.id}
                      checked={checked}
                      disabled={blocked && !checked}
                      onChange={() => toggle(g.id, o.id)}
                      className="size-5 accent-[var(--brand)]"
                    />
                    <span className="flex-1">{o.name}{!o.available && ' (indisponível)'}</span>
                    {o.priceCents > 0 && <span className="text-ink-2">+ {formatBRL(o.priceCents)}</span>}
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}

      {p.allowNotes && canOrder && !unavailable && (
        <div className="mt-5">
          <label htmlFor="item-notes" className="font-semibold">Alguma observação?</label>
          <Textarea id="item-notes" className="mt-2 min-h-20" maxLength={200} placeholder="Ex.: sem cebola, ponto bem passado…" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      )}
      {touched && problem && <div className="mt-4"><Alert tone="danger">{problem}</Alert></div>}
    </Sheet>
  );
}
